
import { Component, Vue, Prop, Model, Watch, Inject } from "vue-property-decorator"
import { State, namespace, Getter, Action } from "vuex-class"

import vSelect from "vue-select"

import { CaseUser, User, CaseField, CaseDetails, TimeZone, Field, Template, DayOfWeek } from "@/types"
import { smeType, smeLabels } from "@/types/sme"

import api from "@/services/api"
import { LabeledValueWithFieldInfo, UserAdvancedSearch, UserWithFields, CurrentUser } from "@/types/user"
import { TimeManagementSettings, DisplayUserTime, UserTimeForWeekWithUser, AvailabilityRange, ShiftPattern } from "@/types/timeManagement"
import { DateTime } from "luxon"
import { UserRole } from "@/types/caseUser"
import DateTimeControl from "@/components/Fields/DateTime.vue"
import { getStartDateOfWeek } from "@/utils/dateUtils"
import RecordAssignmentService from "@/services/recordAssignmentService"

import UserTimeRow from "./UserTimeRow.vue"
import SortHeader from "@/components/Controls/Table/SortHeader.vue"

import { LabeledValue } from "@/types/labeledValue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import DataCache from "@/caching/caching"
import { FieldTypeId } from "@/types/fieldTypes"

const setup = namespace("setup")
const cases = namespace("cases")

interface FieldEnabled {
	userFieldId: string
	recordId: string
	field: CaseField
	enabled: boolean
}

@Component({
	components: {
		"v-select": vSelect,
		"user-time-row": UserTimeRow,
		"date-time": DateTimeControl,
		"sort-header": SortHeader,
		"case-fields": () => import("@/components/New/Cases/EditingCase/CaseFields.vue")
	}
})
export default class TimeAssignment extends Vue {
	@Inject({ from: "recordAssignmentService", default: null }) recordAssignmentService!: RecordAssignmentService

	@Prop({ required: true })
	public currentCase!: CaseDetails;

	@Prop({ required: true })
	public assignmentField!: CaseField

	@Prop({ default: false })
	public readonly!: boolean

	@Prop({ type: Boolean, default: false })
	public canAddCurrentUser!: boolean

	@Model("input", { type: Boolean })
	public isOpen!: boolean

	@State timeManagementSettings!: TimeManagementSettings | null
	@State displayTimezone!: TimeZone | null
	@State timeZones!: TimeZone[]
	@State private readonly userCache!: DataCache<UserWithFields>

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter getDefaultFirstDayOfWeek!: DayOfWeek
	@Getter("getMe") me: CurrentUser | undefined

	@cases.State categories!: Template[]

	@setup.State userFields!: Field[]

	@setup.Action loadUserFieldsIfUnloaded!: () => Promise<void>

	private FieldTypeId = FieldTypeId

	smeUsers: User[] | null = null
	smeUserTimeValues: DisplayUserTime[] = []

	searchUserTimeValues: DisplayUserTime[] = []

	allUserTime: UserTimeForWeekWithUser[] = []

	shiftPatternList: ShiftPattern[] = []

	searchText: string | null = null

	enabledFieldFilters: { [key: string]: boolean } = {}

	filterTimeManagementUsersByShiftPattern: string | null = null

	public availabilityCheckRange: AvailabilityRange | null = null
	private AvailabilityRangeType = AvailabilityRange
	private TenantFeatures = TenantFeatures

	shiftPatternColumnCollapsed: boolean = false

	sortBy = this.availabilityCheckRange === this.AvailabilityRangeType.OnDate ? "assignmentDateRemainingCapacity" : "weeklyRemainingCapacity"
	sortByDescending = true
	/**
	 * Gets the ID of the current case's template.
	 * @returns {string | null} - The ID of the current case's template, or null if not assigned.
	 */
	get assignedTemplateId (): string | null {
		return this.currentCase?.templateId ?? null
	}

	/**
	 * Gets the title of the current case's template. For unsaved (new) cases, this will look up the template from the store's template list.
	 * @returns {string | null} - The title of the current case's template, or null if not set.
	 */
	get assignedTemplateTitle (): string | null {
		if (!this.assignedTemplateId) return null
		if (this.currentCase?.templateTitle) { // Set for saved cases
			return this.currentCase?.templateTitle
		}

		// New cases don't have templateTitle set yet
		const selectedTemplate = this.categories ? this.categories.find(t => t.id === this.assignedTemplateId) : null
		return selectedTemplate ? selectedTemplate.title : null
	}

	/**
	 * Gets the assignment date in ISO format, using assignment date field from time management settings.
	 * @returns {string | null} - The assignment date in ISO format, or null if not available.
	 */
	get assignmentDate (): string | null {
		const assignmentDateField = this.assignmentDateField
		if (!assignmentDateField) return null // The field does not exist on the record

		if (assignmentDateField.value instanceof Date) {
			return assignmentDateField.value.toISOString()
		} else {
			return assignmentDateField.value
		}
	}

	get assignmentDateField (): CaseField | null {
		if (!this.recordAssignmentService || !this.assignmentField || !this.timeManagementSettings) return null
		return this.recordAssignmentService.getAssignmentDate(this.assignmentField.field, this.timeManagementSettings)
	}

	get assignmentDateFieldTitle (): string {
		return this.assignmentDateField?.field?.title ?? "Scheduled Date"
	}

	/**
	 * Gets the SME type associated with the assignment field.
	 * @returns {smeType | null} - The SME type associated with the assignment field, or null if not available.
	 */
	get smeType (): smeType | null {
		return this.assignmentField?.field.extensions?.smeType || null
	}

	/**
	 * Gets the label for the SME type.
	 * @returns {string | null} - The label for the SME type, or null if not available.
	 */
	get smeTypeLabel (): string | null {
		if (!this.smeType) return null
		return smeLabels.findAndGet(l => l.smeType === this.smeType, l => l.label, null)
	}

	/**
	 * Gets the hours field associated with the current field based on the current field's timeManagementHours.
	 * @returns {CaseField | null} - The hours field, or null if not available.
	 */
	get hoursField (): CaseField | null {
		if (!this.assignmentField?.field || !this.currentCase.id) return null

		const roleHoursFieldIdForRecord = this.recordAssignmentService
			.getTimeManagementHoursForRecord(this.assignmentField!.field, this.currentCase)

		return roleHoursFieldIdForRecord
			? this.currentCase.fields?.[roleHoursFieldIdForRecord] ?? null
			: null
	}

	/**
	 * Gets the number of hours for the assignment from the current case.
	 * @returns {number} - The number of hours in the field value.
	 */
	get hours (): number {
		return this.hoursField?.value || 0
	}

	/**
	 * Gets the number of hours remaining on the hours Field of the current case.
	 * @returns {number} - The number of hours remaining out of the total hours.
	 */
	private get hoursRemaining (): number {
		if (!this.hoursField) return 0

		return this.recordAssignmentService
			.hourFieldAssignments[this.hoursField!.field.id!]?.unassignedTime ?? 0
	}

	/**
	 * Gets the append string for the hours field display, showing the hours per person and/or remaining hours.
	 * @returns {string | null} - The append string for the hours field display, or null if not available.
	 */
	private get hoursFieldDisplayAppend (): string | null {
		const appendStrings: string[] = []

		if (this.timeManagementSettings && this.timeManagementSettings.shareHours) {
			const formattedTime = formatFieldsMixin.methods.timeInHoursAndMinutes(this.hoursPerPerson)
			appendStrings.push(`${formattedTime} per assignee`)
		}

		if (this.hoursField?.value !== this.hoursRemaining) {
			const formattedTime =
				formatFieldsMixin.methods.timeInHoursAndMinutes(Math.max(this.hoursRemaining, 0))
			appendStrings.push(`${formattedTime} remaining`)
		}

		return appendStrings.length > 0
			? `(${appendStrings.join(", ")})`
			: null
	}

	/**
	 * Gets the list of already added users on any field that has the hours that match the current field.
	 * @returns {CaseUser[]} - The list of already added users.
	 */
	get alreadyAddedUsers (): CaseUser[] {
		if (!this.assignmentField || !this.currentCase.id) return []

		const roleHoursFieldIdForRecord = this.recordAssignmentService
			.getTimeManagementHoursForRecord(this.assignmentField.field, this.currentCase)

		if (!roleHoursFieldIdForRecord) return []

		const fieldsForHours = this.recordAssignmentService.fieldsByHours[roleHoursFieldIdForRecord]
			?.filter(f => !!f.value) ?? []

		return fieldsForHours.flatMap(field => {
			const fieldValue: UserRole = field.value
			return [
				...(fieldValue.users || []),
				...(fieldValue.subRoles || []).map(sr => sr.users || []).flat()
			]
		})
	}

	/**
	 * Determines if the current user is already added to any field that shares the same hours as the current field.
	 * @returns {Boolean} - True if the current user is already added, false otherwise.
	 */
	get currentUserAlreadyAdded (): Boolean {
		return this.alreadyAddedUsers.some(user => user.id === this.me?.id)
	}

	/**
	 * Gets the hours per person for the assignment.
	 * If shareHours is enabled, it is the number of hours divided by the number of people plus one.
	 * Otherwise, it's the number of remaining hours.
	 * @returns {number} - The assignment hours per person.
	 */
	get hoursPerPerson (): number {
		if (this.timeManagementSettings?.shareHours) {
			return this.hours / (this.alreadyAddedUsers.length + 1)
		}

		return this.hoursRemaining
	}

	/**
	 * Gets the filter fields with their enabled status.
	 * @returns {FieldEnabled[]} - The filter fields with their enabled status.
	 */
	get filterFields (): FieldEnabled[] {
		return this.timeManagementSettings?.userFilterFields
			? this.timeManagementSettings.userFilterFields
				.map(f => ({
					userFieldId: f.userField,
					recordId: f.recordField,
					field: this.currentCase.fields[f.recordField],
					enabled: this.enabledFieldFilters[f.recordField]
				}))
				.filter(f => !!f && f.field && f.field.value && (!Array.isArray(f.field.value) || f.field.value.length > 0))
			: []
	}

	/**
	 * Gets user fields by field ID.
	 * @returns {object} - The dictionary of user fields indexed by field ID.
	 */
	get userFieldsByFieldId (): { [key: string ]: Field } {
		return this.userFields ? this.userFields.toDictionary("id") : {}
	}

	/**
	 * Gets the user search field filters that are enabled.
	 * @returns {object[]} - The applied field filters for user search.
	 */
	get appliedFieldFilters (): { userFieldId: string, values: string[] }[]	{
		return this.filterFields
			.filter(f => f.enabled)
			.map(f => ({
				userFieldId: f.userFieldId,
				values: Array.isArray(f.field.value) ? f.field.value.map(v => v.id) : [f.field.value]
			}))
	}

	/**
	 * Gets the start day of the week based on the assignment date.
	 * @returns {string} - The start day of the week.
	 */
	get weekStart (): string {
		if (!this.assignmentDate) return ""

		const startDayOfWeek = this.getIsFeatureEnabled(TenantFeatures.ScheduleManagementUseTenancyStartDayOfWeek)
			? this.getDefaultFirstDayOfWeek
			: DayOfWeek.Monday

		const assignmentDateTime = DateTime.fromISO(this.assignmentDate)
		const startDateOfWeek = getStartDateOfWeek(assignmentDateTime, startDayOfWeek)
		return startDateOfWeek.toFormat("D")
	}

	get allUnaddedUsers (): UserTimeForWeekWithUser[] {
		const addedSet = new Set<string>(this.alreadyAddedUsers.map(u => u.id))
		return this.allUserTime.filter(u => !addedSet.has(u.userId))
	}

	get allUsersBySmeStatus (): { sme: DisplayUserTime[], other: DisplayUserTime[] } {
		if (!this.assignedTemplateId) return { sme: [], other: [] }

		const fieldSmeType = this.smeType
		return this.allUnaddedUsers.reduce((grouped, user) => {
			const smeList = fieldSmeType === smeType.Production
				? user.user.productionSME
				: fieldSmeType === smeType.Reviewer
					? user.user.reviewerSME
					: null
			const userDisplay = {
				...user,
				name: user.user.name,
				user: {
					...user.user,
					userHeaderFieldValues: user.user.fieldValues ? this.userHeaderFieldValues(user.user.fieldValues) : {}
				}
			}

			if (smeList && smeList.includes(this.assignedTemplateId!)) {
				grouped.sme.push(userDisplay)
			} else {
				grouped.other.push(userDisplay)
			}
			return grouped
		}, { sme: [], other: [] } as { sme: DisplayUserTime[], other: DisplayUserTime[] })
	}

	get smeUserTime (): DisplayUserTime[] {
		let userTime = this.updatedLayout ? this.allUsersBySmeStatus.sme : this.smeUserTimeValues
		userTime = userTime.filter(user => {
			const foundUser = this.userCache.getById(user.userId)
			return foundUser && !foundUser.disabled
		})

		userTime = this.filterByShiftPattern(userTime)

		if (this.searchText) {
			const lowerText = this.searchText.toLowerCase()
			userTime = userTime.filter(u => u.name.toLowerCase().includes(lowerText))
		}
		return userTime.sortBy(this.sortBy as keyof DisplayUserTime, this.sortByDescending)
	}

	get searchUserTime (): DisplayUserTime[] {
		let userTime: DisplayUserTime[] = []
		if (this.updatedLayout) {
			const userTimeMap = new Map<string, DisplayUserTime>()

			// Combine users from both sources
			const allUsers = [...this.allUsersBySmeStatus.other, ...this.searchUserTimeValues]

			allUsers.forEach(user => {
				if (!userTimeMap.has(user.userId)) {
					// Normalize capacity values to 0 if undefined or null
					if (user.weeklyRemainingCapacity === undefined || user.weeklyRemainingCapacity === null) {
						user.weeklyRemainingCapacity = 0
					}
					if (user.assignmentDateRemainingCapacity === undefined || user.assignmentDateRemainingCapacity === null) {
						user.assignmentDateRemainingCapacity = 0
					}
					userTimeMap.set(user.userId, user)
				}
			})

			// Convert the map back to an array
			userTime = Array.from(userTimeMap.values())
		} else {
			userTime = this.searchUserTimeValues
		}

		userTime = userTime.filter(user => {
			const foundUser = this.userCache.getById(user.userId)
			return foundUser && !foundUser.disabled
		})

		userTime = this.filterByShiftPattern(userTime)

		if (this.searchText) {
			const lowerText = this.searchText.toLowerCase()
			userTime = userTime.filter(u => u.name.toLowerCase().includes(lowerText))
		}
		return userTime.sortBy(this.sortBy as keyof DisplayUserTime, this.sortByDescending)
	}

	get updatedLayout (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.TimeManagementDisplayAvailable)
	}

	get showAvailabilityRangeRadioButton (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ScheduleManagementAvailabilityOnDate)
	}

	get showShiftPatternFilter (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.FilterTimeManagementUsersByShift)
	}

	get showShiftPatternColumn (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.TimeManagementShowPatternsInModal) && !this.shiftPatternColumnCollapsed
	}

	get shiftPatternColumnAvailable (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.TimeManagementShowPatternsInModal)
	}

	get ianaTimezone (): string {
		if (this.assignmentDateField && !this.assignmentDateField.valueTimezone) {
			const timezone = this.timeZones.find(tz => tz.id === this.assignmentDateField!.valueTimezone)
			if (timezone) {
				return timezone.iana
			}
		}

		return this.displayTimezone
			? this.displayTimezone.iana
			: Intl.DateTimeFormat().resolvedOptions().timeZone
	}

	get showAddYourselfButtonOnTimeManagementUserRolesFeatureEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ShowAddYourselfButtonOnTimeManagementEnabledUserRoles)
	}

	get addYourselfButtonLabelText (): string {
		let message = ""
		if (this.readonly) {
			message += "Field is set to readonly"
		}
		if (!this.assignmentDate) {
			message += message ? ", and assignment date is not set" : "Assignment date is not set"
		}
		return message
	}

	get shiftPatternTitles (): string[] {
		return this.shiftPatternList.map(pattern => pattern.title || "")
	}

	get sortedShiftPatternList (): ShiftPattern[] {
		return [...this.shiftPatternList].sort((a, b) => {
			const titleA = (a.title || "").toLowerCase()
			const titleB = (b.title || "").toLowerCase()
			return titleA.localeCompare(titleB)
		})
	}

	async mounted () {
		this.availabilityCheckRange = this.getIsFeatureEnabled(TenantFeatures.ScheduleManagementAvailabilityOnDate) ? AvailabilityRange.OnDate : AvailabilityRange.WeekOf
		if (this.isOpen) {
			this.fetchValues()
		}

		await Promise.allSettled([
			this.userCache.fetchIfNeeded(),
			api.shiftPatterns().then(shifts => {
				this.shiftPatternList = shifts
			})
		])
	}

	/**
	 * When the modal opens, ensure user fields are loaded and search for SMEs
	 * Also initialize the enabled field filters to default all filters to enabled
	 * @param {boolean} isOpen - If this modal is open
	 */
	@Watch("isOpen")
	async onOpenChanged (isOpen: boolean) {
		this.searchText = ""
		this.searchUserTimeValues = []

		if (isOpen) {
			this.enabledFieldFilters = this.timeManagementSettings?.userFilterFields
				? this.timeManagementSettings?.userFilterFields
					.toDictionaryMapValue(f => f.recordField, f => true)
				: {}

			await this.loadUserFieldsIfUnloaded()

			this.fetchValues()
		}
	}

	@Watch("assignmentDate")
	onAssignmentDateChanged (newDate: string | null, oldDate: string | null) {
		const newDateTime = newDate ? DateTime.fromISO(newDate) : null
		const oldDateTime = oldDate ? DateTime.fromISO(oldDate) : null

		if (this.isOpen && !!newDate && newDateTime?.toMillis() !== oldDateTime?.toMillis()) {
			this.fetchValues()
		}
	}

	@Watch("currentUserAlreadyAdded")
	onCurrentUserAlreadyAddedChange (newValue: Boolean) {
		this.$emit("current-user-already-added-change", newValue)
	}

	/**
	 * Fetches new layout values for all available users
	 */
	async allUserSearch () {
		if (this.assignedTemplateId && this.assignmentDate) {
			const requireAvailabilityOnDate = this.availabilityCheckRange !== null &&
				this.availabilityCheckRange === AvailabilityRange.OnDate
			const startDayOfWeek = this.getIsFeatureEnabled(TenantFeatures.ScheduleManagementUseTenancyStartDayOfWeek)
				? this.getDefaultFirstDayOfWeek
				: DayOfWeek.Monday

			this.allUserTime = await api.availableUserTimeForDate(this.assignmentDate, this.appliedFieldFilters,
				requireAvailabilityOnDate, startDayOfWeek, this.assignmentDateField?.valueTimezone)
		}
	}

	/**
	 * Performs an SME search based on the assigned template and assignment date.
	 * Updates the `smeUserTime` array with the search results.
	 */
	async smeSearch () {
		if (this.assignedTemplateId && this.assignmentDate) {
			const smeType = this.assignmentField.field.extensions?.smeType

			if (smeType) {
				this.smeUserTimeValues = await this.userLoadTime({
					smeTemplateId: this.assignedTemplateId,
					smeType: smeType,
					userFieldSearches: this.appliedFieldFilters
				})

				this.smeUserTimeValues.forEach(smeUserTime => {
					if (smeUserTime.weeklyRemainingCapacity === undefined || smeUserTime.weeklyRemainingCapacity === null) {
						smeUserTime.weeklyRemainingCapacity = 0
					}
					if (smeUserTime.assignmentDateRemainingCapacity === undefined || smeUserTime.assignmentDateRemainingCapacity === null) {
						smeUserTime.assignmentDateRemainingCapacity = 0
					}
				})
			}
		}
	}

	/**
	 * Performs a user search based on the provided search term.
	 * Updates the `searchUserTime` array with the search results.
	 * @param {string | null} searchTerm - The search term to filter users.
	 */
	async userSearch (searchTerm: string | null) {
		if (!searchTerm) {
			this.searchUserTimeValues = []
			return
		}

		this.searchUserTimeValues = await this.userLoadTime({
			nameFilter: searchTerm,
			userFieldSearches: this.appliedFieldFilters
		})
	}

	/**
	 * Loads user time data based on the provided search criteria.
	 * @param {UserAdvancedSearch} search - The search criteria for loading user time.
	 * @returns {Promise<DisplayUserTime[]>} - The loaded user time data.
	 */
	async userLoadTime (search: UserAdvancedSearch): Promise<DisplayUserTime[]> {
		// Search for users
		const users = (await api.searchUsersAdvanced(search))
			.filter(u => !this.alreadyAddedUsers.some(user => user.id === u.id) &&
			(!this.updatedLayout || !this.allUserTime.some(user => u.id === user.userId)))

		if (users.length === 0) {
			return []
		}

		const startDayOfWeek = this.getIsFeatureEnabled(TenantFeatures.ScheduleManagementUseTenancyStartDayOfWeek)
			? this.getDefaultFirstDayOfWeek
			: DayOfWeek.Monday

		// Get the time for those users on the given date
		const userTime = (await api.userTimeInWeek(this.assignmentDate!, users.map(u => u.id), startDayOfWeek,
			this.assignmentDateField?.valueTimezone))
			.toDictionary("userId")
		return users.map(u => ({
			...userTime[u.id],
			userId: u.id,
			name: u.name,
			user: {
				...u,
				userHeaderFieldValues: u.fieldValues ? this.userHeaderFieldValues(u.fieldValues) : {}
			}
		}))
	}

	/**
	 * Handles the selection of a user from the search results.
	 * Emits the `user-selected` event to communicate the selected user, then closes the modal
	 * @param {DisplayUserTime} user - The selected user.
	 */
	selectUser (user: DisplayUserTime) {
		this.$emit("user-selected", user.user)
		this.$emit("input", false)
	}

	/**
	 * Handles the "Add Yourself" button action.
	 * Emits the `add-self` event to trigger the addition of the current user,
	 * then closes the modal.
	 */
	handleAddSelf () {
		this.$emit("add-self")
		this.$emit("input", false)
	}

	/**
	 * Checks whether the given object is an array. Here to allow isArray usage from within the UI template
	 * @param {any} obj - The object to check.
	 * @returns {boolean} - True if the object is an array, false otherwise.
	 */
	isArray (obj: any): boolean {
		return Array.isArray(obj)
	}

	/**
	 * Filters user time array by shift pattern if a shift pattern filter is active.
	 * @param {DisplayUserTime[]} userTime - The array of user time data to filter.
	 * @returns {DisplayUserTime[]} - The filtered array of user time data.
	 */
	private filterByShiftPattern (userTime: DisplayUserTime[]): DisplayUserTime[] {
		if (this.filterTimeManagementUsersByShiftPattern) {
			return userTime.filter(u => {
				return Array.isArray(u.shiftPatterns) && u.shiftPatterns.some(pattern => pattern.title === this.filterTimeManagementUsersByShiftPattern)
			})
		}
		return userTime
	}

	/**
	 * Converts the user field values into a dictionary with additional field information.
	 * @param {object} fieldValues - The user field values.
	 * @returns {object} - The dictionary of user field values with additional information.
	 */
	userHeaderFieldValues (fieldValues: { [key:string]: LabeledValue }) : { [key: string]: LabeledValueWithFieldInfo } {
		return fieldValues ? Object.keys(fieldValues)
			.map(v => ({
				key: v,
				...fieldValues[v],
				dataType: this.userFieldsByFieldId[v] ? this.userFieldsByFieldId[v].dataType || "" : "",
				fieldTitle: this.userFieldsByFieldId[v] ? this.userFieldsByFieldId[v].title : ""
			}))
			.toDictionary("key")
			: {}
	}

	/**
	 * Fetches updated values from API
	 */
	fetchValues () {
		if (this.updatedLayout) {
			this.allUserSearch()
		} else {
			this.smeSearch()
		}

		if (this.searchText) {
			this.userSearch(this.searchText)
		}
	}

	/**
	 * Sets the enabled state of a field filter and triggers the relevant search operations.
	 * @param {FieldEnabled} filter - The field filter to update.
	 * @param {boolean} enabled - The new enabled state.
	 */
	setFilterEnabled (filter: FieldEnabled, enabled: boolean) {
		Vue.set(this.enabledFieldFilters, filter.recordId, enabled)
		this.fetchValues()
	}

	handleAvailablityCheckRangeChange (newValue: any) {
		this.availabilityCheckRange = newValue
		this.allUserSearch()
	}
}
